import { jsx, jsxs } from "react/jsx-runtime";

/**
 * Shared sub-step pill visuals (themed): accent-ringed when active, soft ok tint when completed, dashed outline when locked/future.
 * The variant context key is still accepted by callers; both variants now sit on themed surfaces and render identically.
 */
export function buildGapSubStepPillParts({ isLocked, isActive, isCompleted, num }) {
  let pillClass;
  let iconWrapClass;
  let iconContent;
  const n = num;
  if (isLocked) {
    iconContent = n;
    pillClass = "bg-transparent text-[color:var(--color-text-muted)] border-dashed border-[color:var(--color-border-strong)] cursor-not-allowed opacity-70";
    iconWrapClass = "bg-[var(--color-bg-alt)] text-[color:var(--color-text-muted)]";
    return { pillClass, iconWrapClass, iconContent };
  }
  if (isActive) {
    pillClass = "bg-[var(--color-info-bg)] text-[color:var(--color-text-hero)] border-[color:var(--color-accent)] shadow-[0_0_0_4px_color-mix(in_srgb,var(--color-accent)_18%,transparent)]";
    if (isCompleted) {
      iconWrapClass = "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] ring-1 ring-inset ring-[color:var(--color-ok-line)] text-[10px]";
      iconContent = "✓";
    } else {
      iconWrapClass = "bg-[var(--color-accent-fill)] text-white text-[9px]";
      iconContent = n;
    }
    return { pillClass, iconWrapClass, iconContent };
  }
  if (isCompleted) {
    iconContent = "✓";
    pillClass = "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)] hover:border-[color:var(--color-ok-fg)]";
    iconWrapClass = "bg-[var(--color-bg-white)] text-[color:var(--color-ok-fg)] text-[10px]";
    return { pillClass, iconWrapClass, iconContent };
  }
  iconContent = n;
  pillClass = "bg-transparent text-[color:var(--color-text-muted)] border-dashed border-[color:var(--color-border-strong)] hover:border-[color:var(--color-accent)]";
  iconWrapClass = "bg-[var(--color-bg-alt)] text-[color:var(--color-text-muted-dark)] text-[9px]";
  return { pillClass, iconWrapClass, iconContent };
}

function buildAttentionDotLabel(attentionTitle, badgeCount) {
  const t = typeof attentionTitle === "string" ? attentionTitle.trim() : "";
  const n = Number(badgeCount) || 0;
  if (t && n > 0) return `${t} · ${n} meeting request(s)`;
  if (t) return t;
  if (n > 0) return `${n} meeting request(s)`;
  return "";
}

export function renderGapSubStepNavButton(s, ctx) {
  const {
    isLocked,
    isActive,
    isCompleted,
    badgeCount = 0,
    attentionTitle: attentionTitleRaw,
    lockedHint,
    compact = false,
    onSelect
  } = ctx;
  const attentionTitle = typeof attentionTitleRaw === "string" ? attentionTitleRaw.trim() : "";
  const showAttentionDot = attentionTitle.length > 0;
  const nBadge = Number(badgeCount) || 0;
  const showMeetingDot = nBadge > 0;
  const showDot = showAttentionDot || showMeetingDot;
  const dotLabel = buildAttentionDotLabel(attentionTitle, nBadge);
  const parts = buildGapSubStepPillParts({
    isLocked,
    isActive,
    isCompleted,
    num: s.num
  });
  const pad = compact ? "px-3 py-1.5 rounded-lg" : "px-4 py-2.5 rounded-xl";
  const textSize = compact ? "text-[9px]" : "text-[10px]";
  const lockedHintStr = typeof lockedHint === "string" ? lockedHint.trim() : "";
  let buttonTitle = isLocked
    ? lockedHintStr || "Locked - complete previous steps to unlock"
    : isCompleted && !isActive
      ? "Completed - view only"
      : undefined;
  if (!isLocked && showAttentionDot && attentionTitle) {
    buttonTitle = buttonTitle ? `${buttonTitle} - ${attentionTitle}` : attentionTitle;
  }
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick: () => { if (!isLocked) onSelect(s.key); },
      disabled: isLocked,
      title: buttonTitle,
      className: `inline-flex items-center gap-2 ${pad} ${textSize} font-black uppercase tracking-widest transition-all shadow-sm border ${parts.pillClass}`,
      children: [
        /* @__PURE__ */ jsx("span", {
          className: `inline-flex items-center justify-center min-w-[1.25rem] h-5 rounded-full font-bold shrink-0 ${parts.iconWrapClass}`,
          children: parts.iconContent
        }),
        /* @__PURE__ */ jsxs("span", {
          className: "inline-flex items-center gap-2 min-w-0",
          children: [
            s.label,
            showDot && /* @__PURE__ */ jsx("span", {
              className: "h-2 w-2 rounded-full bg-[var(--color-danger-fg)] shrink-0",
              title: dotLabel || undefined,
              "aria-label": dotLabel || undefined
            })
          ]
        })
      ]
    },
    s.key
  );
}
